import { Component, OnInit, Input } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators, FormArray, FormGroupDirective } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { HttpEventType } from '@angular/common/http';
import { ProcessingInformation, TerminalTypeCode, CreditCardProcessingTechnologyCode, PersonValidator, PersonService, AccountStatus, MerchantAccount, MerchantAccountType, Attachment, AttachmentTypeCode, AttachmentService } from '../../../shared';

@Component({
    selector: 'app-processing-information',
    templateUrl: './processing-information.component.html'
})
export class ProcessingInformationComponent implements OnInit {

    @Input() formGroupName: string;
    @Input() showErrors: boolean;

    disableWebsitePayment: boolean = true;
    isCashnet: boolean;

    processingInformation: ProcessingInformation;
    processingInformationFormGroup: FormGroup;
    get itSupportPerson(): AbstractControl {
        return this.processingInformationFormGroup.get('itSupportPerson');
    }
    get creditCardProcessingTechnologyCd(): AbstractControl {
        return this.processingInformationFormGroup.get('creditCardProcessingTechnologyCd');
    }
    get terminalTypeCd(): AbstractControl {
        return this.processingInformationFormGroup.get('terminalTypeCd');
    }
    get paymentGatewayVendor(): AbstractControl {
        return this.processingInformationFormGroup.get('paymentGatewayVendor');
    }
    get numberOfTerminals(): AbstractControl {
        return this.processingInformationFormGroup.get('numberOfTerminals');
    }
    get systemName(): AbstractControl {
        return this.processingInformationFormGroup.get('systemName');
    }
    get systemVersion(): AbstractControl {
        return this.processingInformationFormGroup.get('systemVersion');
    }
    get posPaymentGatewayVendor(): AbstractControl {
        return this.processingInformationFormGroup.get('posPaymentGatewayVendor');
    }
    get posHardware(): AbstractControl {
        return this.processingInformationFormGroup.get('posHardware');
    }
    get businessJustification(): AbstractControl {
        return this.processingInformationFormGroup.get('businessJustification');
    }
    get pciComplianceDocument(): AbstractControl {
        return this.processingInformationFormGroup.get('pciComplianceDocument');
    }
    get processingStaffPeople(): FormArray {
        return this.processingInformationFormGroup.get('processingStaffPeople') as FormArray;
    }
    get computerNames(): FormArray {
        return this.processingInformationFormGroup.get('computerNames') as FormArray;
    }

    constructor(
        private readonly rootFormGroup: FormGroupDirective,
        private readonly fb: FormBuilder,
        private readonly route: ActivatedRoute,
        private readonly personService: PersonService,
        private readonly attachmentService: AttachmentService,
    ) {
    }

    ngOnInit() {
        this.route.data.subscribe(({ merchantAccount }: { merchantAccount: MerchantAccount }) => {
            this.isCashnet = merchantAccount.merchantAccountType === MerchantAccountType.Cashnet ? true : null;
            this.processingInformation = merchantAccount.processingInformation;
            var processingStaffFormArray = this.processingInformation.processingStaffPeople.length > 0 ? this.processingInformation.processingStaffPeople.map((processingStaff) => {
                return this.fb.control(processingStaff, [Validators.required]);
            }) : [];
            var computerNamesFormArray = this.processingInformation.computerNames.length > 0 ? this.processingInformation.computerNames.map((computerName) => {
                return this.fb.control(computerName, [Validators.required]);
            }) : [];
            this.processingInformationFormGroup = this.rootFormGroup.control.get(this.formGroupName) as FormGroup;
            this.processingInformationFormGroup.addControl("creditCardProcessingTechnologyCd", this.fb.control(this.processingInformation.creditCardProcessingTechnologyCd, [Validators.required]));
            this.processingInformationFormGroup.addControl("itSupportPerson", this.fb.control(this.processingInformation.itSupportPerson));
            this.processingInformationFormGroup.addControl("terminalTypeCd", this.fb.control(this.processingInformation.terminalTypeCd));
            this.processingInformationFormGroup.addControl("paymentGatewayVendor", this.fb.control(this.processingInformation.paymentGatewayVendor));
            this.processingInformationFormGroup.addControl("numberOfTerminals", this.fb.control(this.processingInformation.numberOfTerminals));
            this.processingInformationFormGroup.addControl("systemName", this.fb.control(this.processingInformation.systemName));
            this.processingInformationFormGroup.addControl("systemVersion", this.fb.control(this.processingInformation.systemVersion));
            this.processingInformationFormGroup.addControl("posPaymentGatewayVendor", this.fb.control(this.processingInformation.posPaymentGatewayVendor));
            this.processingInformationFormGroup.addControl("posHardware", this.fb.control(this.processingInformation.posHardware));
            this.processingInformationFormGroup.addControl("businessJustification", this.fb.control(this.processingInformation.businessJustification));
            this.processingInformationFormGroup.addControl("pciComplianceDocument", this.fb.control(this.processingInformation.pciComplianceDocument));
            this.processingInformationFormGroup.addControl("processingStaffPeople", this.fb.array(processingStaffFormArray));
            this.processingInformationFormGroup.addControl("computerNames", this.fb.array(computerNamesFormArray));
            this.setCreditCardProcessingTechnologyCdValidators(this.processingInformation.creditCardProcessingTechnologyCd);
            this.setTerminalTypeCdValidators(this.processingInformation.terminalTypeCd);
            if (!this.isCashnet) {
                this.disableWebsitePayment = merchantAccount.salesInformation.websiteCommerceCardAcceptanceChannel === 0 || merchantAccount.salesInformation.websiteCommerceCardAcceptanceChannel === null;
            } else {
                this.disableWebsitePayment = null;
            }
            if (merchantAccount.status === AccountStatus.Complete || merchantAccount.status === AccountStatus.Finalized) {
                this.processingInformationFormGroup.disable();
            }
            this.creditCardProcessingTechnologyCd.valueChanges.subscribe((value: CreditCardProcessingTechnologyCode) => {
                this.creditCardProcessingTechnologyCdChange(value);
            });
            this.terminalTypeCd.valueChanges.subscribe((value: TerminalTypeCode) => {
                this.terminalTypeCdChange(value);
            });
        });
    }

    creditCardProcessingTechnologyCdChange(creditCardProcessingTechnologyCode: CreditCardProcessingTechnologyCode) {
        console.log("creditCardProcessingTechnologyCdChange")
        if (this.terminalTypeCd.value != null) {
            this.terminalTypeCd.setValue(null);
            this.terminalTypeCd.updateValueAndValidity();
        }
        this.paymentGatewayVendor.setValue(null);
        this.paymentGatewayVendor.updateValueAndValidity();
        this.itSupportPerson.setValue(null);
        this.itSupportPerson.updateValueAndValidity();
        this.numberOfTerminals.setValue(null);
        this.numberOfTerminals.updateValueAndValidity();
        this.systemName.setValue(null);
        this.systemName.updateValueAndValidity();
        this.systemVersion.setValue(null);
        this.systemVersion.updateValueAndValidity();
        this.posPaymentGatewayVendor.setValue(null);
        this.posPaymentGatewayVendor.updateValueAndValidity();
        this.posHardware.setValue(null);
        this.posHardware.updateValueAndValidity();
        this.businessJustification.setValue(null);
        this.businessJustification.updateValueAndValidity();
        this.pciComplianceDocument.setValue(null);
        this.pciComplianceDocument.updateValueAndValidity();
        this.processingStaffPeople.setValue([]);
        this.processingStaffPeople.updateValueAndValidity();
        this.computerNames.setValue([]);
        this.computerNames.updateValueAndValidity();
        this.setCreditCardProcessingTechnologyCdValidators(creditCardProcessingTechnologyCode);
    }

    setCreditCardProcessingTechnologyCdValidators(creditCardProcessingTechnologyCode: CreditCardProcessingTechnologyCode) {
        console.log("setCreditCardProcessingTechnologyCdValidators")
        switch (creditCardProcessingTechnologyCode) {
        case CreditCardProcessingTechnologyCode.Website:
            this.setWebsiteValidators();
            break;
        case CreditCardProcessingTechnologyCode.InPersonMOTO:
            this.setInPersonMOTOValidators();
            break;
        case CreditCardProcessingTechnologyCode.PointOfSale:
            this.setPointOfSaleValidators();
            break;
        }
    }

    terminalTypeCdChange(terminalTypeCode: TerminalTypeCode) {
        console.log("terminalTypeCdChange")
        this.paymentGatewayVendor.setValue(null);
        this.paymentGatewayVendor.updateValueAndValidity();
        this.itSupportPerson.setValue(null);
        this.itSupportPerson.updateValueAndValidity();
        this.numberOfTerminals.setValue(null);
        this.numberOfTerminals.updateValueAndValidity();
        this.systemName.setValue(null);
        this.systemName.updateValueAndValidity();
        this.systemVersion.setValue(null);
        this.systemVersion.updateValueAndValidity();
        this.posPaymentGatewayVendor.setValidators(null);
        this.posPaymentGatewayVendor.updateValueAndValidity();
        this.posHardware.setValue(null);
        this.posHardware.updateValueAndValidity();
        this.businessJustification.setValue(null);
        this.businessJustification.updateValueAndValidity();
        this.pciComplianceDocument.setValue(null);
        this.pciComplianceDocument.updateValueAndValidity();
        this.processingStaffPeople.setValue([]);
        this.processingStaffPeople.updateValueAndValidity();
        this.computerNames.setValue([]);
        this.computerNames.updateValueAndValidity();
        this.setTerminalTypeCdValidators(terminalTypeCode);
    }

    setTerminalTypeCdValidators(terminalTypeCode: TerminalTypeCode) {
        console.log("setTerminalTypeCdValidators")
        switch (terminalTypeCode) {
        case TerminalTypeCode.DialUp:
            this.setDialUpValidators();
            break;
        case TerminalTypeCode.IP:
            this.setIPValidators();
            break;
        case TerminalTypeCode.Cellular:
            this.setCellularValidators();
            break;
        case TerminalTypeCode.Virtual:
            this.setVirtualValidators();
            break;
        }
    }

    setWebsiteValidators() {
        console.log("setWebsiteValidators")
        //required
        if (!this.isCashnet) {
            this.paymentGatewayVendor.setValidators([Validators.required]);
            this.paymentGatewayVendor.updateValueAndValidity();
            this.itSupportPerson.setValidators([Validators.required]);
            this.itSupportPerson.updateValueAndValidity();
        } else {
            this.paymentGatewayVendor.setValidators(null);
            this.paymentGatewayVendor.updateValueAndValidity();
            this.itSupportPerson.setValidators(null);
            this.itSupportPerson.setAsyncValidators(null);
            this.itSupportPerson.updateValueAndValidity();
        }

        //N/A
        this.terminalTypeCd.setValidators(null);
        this.terminalTypeCd.updateValueAndValidity({ emitEvent: false });
        this.numberOfTerminals.setValidators(null);
        this.numberOfTerminals.updateValueAndValidity();
        this.systemName.setValidators(null);
        this.systemName.updateValueAndValidity();
        this.systemVersion.setValidators(null);
        this.systemVersion.updateValueAndValidity();
        this.posPaymentGatewayVendor.setValidators(null);
        this.posPaymentGatewayVendor.updateValueAndValidity();
        this.posHardware.setValidators(null);
        this.posHardware.updateValueAndValidity();
        this.businessJustification.setValidators(null);
        this.businessJustification.updateValueAndValidity();
        this.pciComplianceDocument.setValidators(null);
        this.pciComplianceDocument.updateValueAndValidity();
        this.processingStaffPeople.controls.forEach(control => {
            control.setValidators(null);
            control.setAsyncValidators(null);
            control.updateValueAndValidity();
        });
        this.computerNames.controls.forEach(control => {
            control.setValidators(null);
            control.updateValueAndValidity();
        });
    }

    setInPersonMOTOValidators() {
        console.log("setInPersonMOTOValidators")
        this.terminalTypeCd.setValidators([Validators.required]);
        this.terminalTypeCd.updateValueAndValidity();
    }

    setDialUpValidators() {
        console.log("setDialUpValidators")
        //required
        this.numberOfTerminals.setValidators([Validators.required]);
        this.numberOfTerminals.updateValueAndValidity();
        this.itSupportPerson.setValidators([Validators.required]);
        this.itSupportPerson.updateValueAndValidity();

        //N/A
        this.paymentGatewayVendor.setValidators(null);
        this.paymentGatewayVendor.updateValueAndValidity();
        this.systemName.setValidators(null);
        this.systemName.updateValueAndValidity();
        this.systemVersion.setValidators(null);
        this.systemVersion.updateValueAndValidity();
        this.posPaymentGatewayVendor.setValidators(null);
        this.posPaymentGatewayVendor.updateValueAndValidity();
        this.posHardware.setValidators(null);
        this.posHardware.updateValueAndValidity();
        this.businessJustification.setValidators(null);
        this.businessJustification.updateValueAndValidity();
        this.pciComplianceDocument.setValidators(null);
        this.pciComplianceDocument.updateValueAndValidity();
        this.processingStaffPeople.controls.forEach(control => {
            control.setValidators(null);
            control.setAsyncValidators(null);
            control.updateValueAndValidity();
        });
        this.computerNames.controls.forEach(control => {
            control.setValidators(null);
            control.updateValueAndValidity();
        });
    }

    setIPValidators() {
        console.log("setIPValidators")
        //required
        this.numberOfTerminals.setValidators([Validators.required]);
        this.numberOfTerminals.updateValueAndValidity();
        this.itSupportPerson.setValidators([Validators.required]);
        this.itSupportPerson.updateValueAndValidity();

        //N/A
        this.paymentGatewayVendor.setValidators(null);
        this.paymentGatewayVendor.updateValueAndValidity();
        this.systemName.setValidators(null);
        this.systemName.updateValueAndValidity();
        this.systemVersion.setValidators(null);
        this.systemVersion.updateValueAndValidity();
        this.posPaymentGatewayVendor.setValidators(null);
        this.posPaymentGatewayVendor.updateValueAndValidity();
        this.posHardware.setValidators(null);
        this.posHardware.updateValueAndValidity();
        this.businessJustification.setValidators(null);
        this.businessJustification.updateValueAndValidity();
        this.pciComplianceDocument.setValidators(null);
        this.pciComplianceDocument.updateValueAndValidity();
        this.processingStaffPeople.controls.forEach(control => {
            control.setValidators(null);
            control.setAsyncValidators(null);
            control.updateValueAndValidity();
        });
        this.computerNames.controls.forEach(control => {
            control.setValidators(null);
            control.updateValueAndValidity();
        });
    }

    setCellularValidators() {
        console.log("setCellularValidators")
        //required
        this.numberOfTerminals.setValidators([Validators.required]);
        this.numberOfTerminals.updateValueAndValidity();
        this.itSupportPerson.setValidators([Validators.required]);
        this.itSupportPerson.updateValueAndValidity();

        //N/A
        this.paymentGatewayVendor.setValidators(null);
        this.paymentGatewayVendor.updateValueAndValidity();
        this.systemName.setValidators(null);
        this.systemName.updateValueAndValidity();
        this.systemVersion.setValidators(null);
        this.systemVersion.updateValueAndValidity();
        this.posPaymentGatewayVendor.setValidators(null);
        this.posPaymentGatewayVendor.updateValueAndValidity();
        this.posHardware.setValidators(null);
        this.posHardware.updateValueAndValidity();
        this.businessJustification.setValidators(null);
        this.businessJustification.updateValueAndValidity();
        this.pciComplianceDocument.setValidators(null);
        this.pciComplianceDocument.updateValueAndValidity();
        this.processingStaffPeople.controls.forEach(control => {
            control.setValidators(null);
            control.setAsyncValidators(null);
            control.updateValueAndValidity();
        });
        this.computerNames.controls.forEach(control => {
            control.setValidators(null);
            control.updateValueAndValidity();
        });
    }

    setVirtualValidators() {
        console.log("setVirtualValidators")
        //required
        this.itSupportPerson.setValidators([Validators.required]);
        this.itSupportPerson.updateValueAndValidity();
        this.processingStaffPeople.controls.forEach(control => {
            control.setValidators([Validators.required]);
            control.updateValueAndValidity();
        });
        this.computerNames.controls.forEach(control => {
            control.setValidators([Validators.required]);
            control.updateValueAndValidity();
        });

        //N/A
        this.paymentGatewayVendor.setValidators(null);
        this.paymentGatewayVendor.updateValueAndValidity();
        this.numberOfTerminals.setValidators(null);
        this.numberOfTerminals.updateValueAndValidity();
        this.systemName.setValidators(null);
        this.systemName.updateValueAndValidity();
        this.systemVersion.setValidators(null);
        this.systemVersion.updateValueAndValidity();
        this.posPaymentGatewayVendor.setValidators(null);
        this.posPaymentGatewayVendor.updateValueAndValidity();
        this.posHardware.setValidators(null);
        this.posHardware.updateValueAndValidity();
        this.businessJustification.setValidators(null);
        this.businessJustification.updateValueAndValidity();
        this.pciComplianceDocument.setValidators(null);
        this.pciComplianceDocument.updateValueAndValidity();
    }

    setPointOfSaleValidators() {
        console.log("setPointOfSaleValidators")
        //required
        this.numberOfTerminals.setValidators([Validators.required]);
        this.numberOfTerminals.updateValueAndValidity();
        this.itSupportPerson.setValidators([Validators.required]);
        this.itSupportPerson.updateValueAndValidity();
        this.systemName.setValidators([Validators.required]);
        this.systemName.updateValueAndValidity();
        this.systemVersion.setValidators([Validators.required]);
        this.systemVersion.updateValueAndValidity();
        this.posPaymentGatewayVendor.setValidators([Validators.required]);
        this.posPaymentGatewayVendor.updateValueAndValidity();
        this.posHardware.setValidators([Validators.required]);
        this.posHardware.updateValueAndValidity();
        this.businessJustification.setValidators([Validators.required]);
        this.businessJustification.updateValueAndValidity();
        this.pciComplianceDocument.setValidators([Validators.required]);
        this.pciComplianceDocument.updateValueAndValidity();

        //N/A
        this.paymentGatewayVendor.setValidators(null);
        this.paymentGatewayVendor.updateValueAndValidity();
        this.terminalTypeCd.setValidators(null);
        this.terminalTypeCd.updateValueAndValidity({emitEvent: false});
        this.processingStaffPeople.controls.forEach(control => {
            control.setValidators(null);
            control.setAsyncValidators(null);
            control.updateValueAndValidity();
        });
        this.computerNames.controls.forEach(control => {
            control.setValidators(null);
            control.updateValueAndValidity();
        });
    }

    toggleDisableWebsiteCreditCardProcessingTechnology(disableWebsitePayment: boolean): void {
        if (disableWebsitePayment) {
            this.disableWebsitePayment = true;
            this.creditCardProcessingTechnologyCd.setValue(null);
        } else {
            this.disableWebsitePayment = null;
        }
    }

    onDeleteItSupportPerson() {
        this.itSupportPerson.setValue(null);
        this.itSupportPerson.updateValueAndValidity();
    }

    showAddProcessingStaff(): boolean {
        return this.processingStaffPeople.valid &&
            this.processingStaffPeople.value.every((person) => {
                return person != null;
            });
    }

    onAddProcessingStaff(): void {
        this.processingStaffPeople.push(this.fb.control(null, [Validators.required]));
    }

    onDeleteProcessingStaffPerson(index: any): void {
        if (this.processingStaffPeople.length !== 1) {
            this.processingStaffPeople.removeAt(index);
        } else {
            var processingStaffPerson = this.processingStaffPeople.at(index);
            processingStaffPerson.setValue(null);
            processingStaffPerson.updateValueAndValidity();
        }
    }

    showAddComputerName(): boolean {
        return this.computerNames.valid &&
            this.computerNames.value.every((computerName) => {
                return computerName != null;
            });
    }

    onAddComputerName(): void {
        this.computerNames.push(this.fb.control(null, [Validators.required]));
    }

    onDeleteComputerName(index: any): void {
        if (this.computerNames.length !== 1) {
            this.computerNames.removeAt(index);
        } else {
            var computerName = this.computerNames.at(index);
            computerName.setValue(null);
            computerName.updateValueAndValidity();
        }
    }

    updateFileValue(files: File[]) {
        var file = files[0];
        const formData = new FormData();
        formData.append('file', file, file.name);
        this.attachmentService.uploadPCIComplianceDocument(formData).subscribe((attachment) => {
            this.pciComplianceDocument.setValue(attachment);
            this.pciComplianceDocument.updateValueAndValidity();
        });
    }

    readFile() {
        this.attachmentService.downloadPCIComplianceDocument(this.pciComplianceDocument.value).subscribe(
            (event) => {
                if (event.type === HttpEventType.Response) {
                    const downloadedFile = new Blob([event.body], { type: event.body.type });
                    const a = document.createElement('a');
                    a.setAttribute('style', 'display:none;');
                    document.body.appendChild(a);
                    a.download = this.pciComplianceDocument.value.fileName;
                    a.href = URL.createObjectURL(downloadedFile);
                    a.target = '_blank';
                    a.click();
                    document.body.removeChild(a);
                }
            });
    }
}